/** @format */

import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { api, getApiUrl } from '../api.ts';
import {
  planningFocusesFromApi,
  presenceUsersFromApi,
} from '../components/app/planning/utils/planningPresence';
import { selectAuth } from '../redux/authSlice';
import { fetchSite } from '../redux/leadSlice';
import CacheManager from '../utils/cache';

export const MESSAGE_QUEUE_UPDATED = 'message-queue-updated';
export const UNASSIGNED_LEADS_UPDATED = 'unassigned-leads-updated';
export const CACHE_INVALIDATED = 'cache-invalidated';
export const PLANNING_CHANGED = 'planning-changed';
export const PLANNING_REMOTE_TRIP_UPDATE = 'planning-remote-trip-update';
export const PLANNING_REMOTE_TRIP_DELETE = 'planning-remote-trip-delete';
export const PLANNING_OUTBOUND_MESSAGE = 'planning-outbound-message';

const UserWebSocketContext = createContext();

function optimisticFocusFromLocation(user, location) {
  if (!user?.id || !location) return null;
  const [row] = planningFocusesFromApi({
    focuses: [
      {
        user: {
          id: user.id,
          first_name: user.first_name,
          last_name: user.last_name,
          full_name: `${user.first_name || ''} ${user.last_name || ''}`.trim(),
        },
        chantierId: location.chantierId,
        tripId: location.tripId,
        technicianId: location.technicianId,
        layerId: location.layerId,
        planId: location.planId,
      },
    ],
  });
  return row || null;
}

export const useUserWebSocket = () => useContext(UserWebSocketContext);

function UserWebSocketProvider({ children }) {
  const [isConnected, setIsConnected] = useState(false);
  const [planningPresenceUsers, setPlanningPresenceUsers] = useState([]);
  const [planningFocuses, setPlanningFocuses] = useState([]);

  const { user, isAuthenticated } = useSelector(selectAuth);
  const { enqueueSnackbar } = useSnackbar();

  const ws = useRef(null);
  const dispatch = useDispatch();
  const subscribedChannelsRef = useRef(new Set());
  const userRef = useRef(user);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const stoppedRef = useRef(false);
  userRef.current = user;

  const callbackRegistry = {
    fetchSite: () => {
      dispatch(fetchSite());
    },
  };

  const sendWsPayload = useCallback((payload) => {
    if (ws.current?.readyState !== WebSocket.OPEN) return;
    ws.current.send(JSON.stringify(payload));
  }, []);

  const resubscribeChannels = useCallback(() => {
    subscribedChannelsRef.current.forEach((channel) => {
      sendWsPayload({ action: 'subscribe', channel });
    });
  }, [sendWsPayload]);

  const callback = (content) => {
    const { type, data } = content;
    if (type === 'notification') {
      enqueueSnackbar(data.content, {
        variant: 'webSocketNotification',
        persist: data.persist || false,
        autoHideDuration: data.duration || 5000,
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        title: data.title,
        customColor: data.color,
        customIcon: data.icon,
        customUserAvatarId: data.userAvatarId,
        customIsImportant: data.important,
      });
    }

    if (type === 'automaticmessagequeueentry') {
      const event = new CustomEvent(MESSAGE_QUEUE_UPDATED);
      window.dispatchEvent(event);
    }

    if (type === 'unassigned_leads') {
      setTimeout(() => {
        const event = new CustomEvent(UNASSIGNED_LEADS_UPDATED);
        window.dispatchEvent(event);
      }, 1000);
    }

    if (type === 'cache_invalidate') {
      const modelKey = data?.model;
      if (modelKey) {
        const model = CacheManager.instance.getModel(modelKey);
        CacheManager.instance.invalidateModel(modelKey);
        const notifyInvalidated = () => {
          window.dispatchEvent(
            new CustomEvent(CACHE_INVALIDATED, {
              detail: { model: modelKey },
            }),
          );
        };
        if (model?.isLocalOnly) {
          notifyInvalidated();
        } else if (model) {
          model
            .update()
            .then(notifyInvalidated)
            .catch((err) => {
              console.error(`Error refetching cache for ${modelKey}:`, err);
            });
        }
      }
    }

    if (type === 'planning_change') {
      window.dispatchEvent(
        new CustomEvent(PLANNING_CHANGED, {
          detail: data,
        }),
      );
    }

    if (type === 'planning_outbound_message') {
      enqueueSnackbar(data.message, {
        variant: 'webSocketNotification',
        persist: Boolean(data.persist),
        ...(data.persist ? {} : { autoHideDuration: 5000 }),
        anchorOrigin: { vertical: 'top', horizontal: 'center' },
        title: data.title,
        customColor: data.color,
        customIcon: data.icon,
      });
      window.dispatchEvent(
        new CustomEvent(PLANNING_OUTBOUND_MESSAGE, {
          detail: data,
        }),
      );
    }

    if (type === 'async_file_download') {
      const callbackUrl = data.callback_url;
      const fileName = data.filename;
      const { method, cb } = data;

      if (method === 'GET') {
        api.get(callbackUrl, {}).then((response) => {
          const blob = new Blob([response.data], { type: 'text/csv' });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.target = '_blank';
          link.download = fileName;
          link.click();
          window.URL.revokeObjectURL(link.href);
          if (cb && callbackRegistry[cb]) {
            callbackRegistry[cb]();
          }
        });
      }
    }
  };

  const subscribeChannel = useCallback(
    (channel) => {
      if (!channel) return;
      subscribedChannelsRef.current.add(channel);
      sendWsPayload({ action: 'subscribe', channel });
    },
    [sendWsPayload],
  );

  const unsubscribeChannel = useCallback(
    (channel) => {
      if (!channel) return;
      subscribedChannelsRef.current.delete(channel);
      sendWsPayload({ action: 'unsubscribe', channel });
    },
    [sendWsPayload],
  );

  const sendPlanningFocus = useCallback(
    (location) => {
      const currentUser = userRef.current;
      if (!location) {
        sendWsPayload({ action: 'planning_focus' });
        if (currentUser?.id) {
          setPlanningFocuses((prev) =>
            prev.filter(
              (row) => Number(row.user?.id) !== Number(currentUser.id),
            ),
          );
        }
        return;
      }
      sendWsPayload({
        action: 'planning_focus',
        chantierId: location.chantierId ?? null,
        tripId: location.tripId ?? null,
        technicianId: location.technicianId ?? null,
        layerId: location.layerId ?? null,
        planId: location.planId ?? null,
      });
      const optimistic = optimisticFocusFromLocation(currentUser, location);
      if (!optimistic) return;
      setPlanningFocuses((prev) => {
        const others = prev.filter(
          (row) => Number(row.user?.id) !== Number(optimistic.user.id),
        );
        return [...others, optimistic];
      });
    },
    [sendWsPayload],
  );

  const connect = useCallback(() => {
    if (stoppedRef.current || ws.current) return;
    try {
      const url = getApiUrl().replace(/^http(s?):\/\//, 'ws$1://');
      ws.current = new WebSocket(`${url}/ws/me/`);
    } catch (e) {
      console.error('Unable to connect to WS...', e);
      return;
    }
    ws.current.onopen = () => {
      reconnectAttemptRef.current = 0;
      setIsConnected(true);
      resubscribeChannels();
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      heartbeatTimerRef.current = setInterval(() => {
        sendWsPayload({ action: 'ping' });
      }, 25000);
    };
    ws.current.onclose = () => {
      setIsConnected(false);
      setPlanningPresenceUsers([]);
      setPlanningFocuses([]);
      ws.current = null;
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      if (stoppedRef.current || !userRef.current) return;
      const attempt = reconnectAttemptRef.current;
      reconnectAttemptRef.current += 1;
      const delay = Math.min(30000, 1000 * 2 ** attempt) + Math.random() * 500;
      reconnectTimerRef.current = setTimeout(() => {
        connect();
      }, delay);
    };
    ws.current.onmessage = (message) => {
      try {
        const data = JSON.parse(message.data);
        if (data?.type === 'pong') return;
        if (data?.type === 'planning_presence') {
          setPlanningPresenceUsers(presenceUsersFromApi(data.data));
          return;
        }
        if (data?.type === 'planning_focus') {
          setPlanningFocuses(planningFocusesFromApi(data.data));
          return;
        }
        callback(data);
      } catch (err) {
        console.error('Invalid websocket payload', err);
      }
    };
  }, [resubscribeChannels, sendWsPayload]);

  useEffect(
    () => () => {
      stoppedRef.current = true;
      if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
      if (heartbeatTimerRef.current) clearInterval(heartbeatTimerRef.current);
      if (ws.current) ws.current.close();
    },
    [],
  );

  useEffect(() => {
    if (isAuthenticated && user) {
      stoppedRef.current = false;
      connect();
    } else {
      stoppedRef.current = true;
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      if (ws.current) {
        ws.current.close();
        ws.current = null;
      }
      setIsConnected(false);
      setPlanningPresenceUsers([]);
      setPlanningFocuses([]);
    }
  }, [isAuthenticated, user, connect]);

  const value = useMemo(
    () => ({
      subscribeChannel,
      unsubscribeChannel,
      isWSConnected: isConnected,
      sendPlanningFocus,
      planningPresenceUsers,
      planningFocuses,
    }),
    [
      subscribeChannel,
      unsubscribeChannel,
      isConnected,
      sendPlanningFocus,
      planningPresenceUsers,
      planningFocuses,
    ],
  );

  return (
    <UserWebSocketContext.Provider value={value}>
      {children}
    </UserWebSocketContext.Provider>
  );
}

UserWebSocketProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { UserWebSocketContext, UserWebSocketProvider };
